import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { hasEntitlement } from '../utils/entitlements';
import { experimentsAPI, pulsescanAPI } from '../services/api';
import { useVisibleInterval } from './useVisibleInterval';

const POLL_MS_ACTIVE = 6000;
const POLL_MS_QUIET = 20000;

/**
 * Lightweight, sidebar-friendly probe for "does this service currently have a
 * job/run in flight for the selected project?".
 *
 * Returns a stable map keyed by sidebar `service` identifiers (matches
 * `entitlements.js`):
 *   { ab_testing, cwv, heatmaps, clickpath, campaigns, seomax, swiss }
 *
 * Detection sources (interim, real APIs only — no synthetic flags):
 *   - cwv         → PulseScan listSessions(status=queued,running, limit=1)
 *   - ab_testing  → experimentsAPI.getAll → any experiment with status==='running'
 *
 * Other services (heatmaps, clickpath, campaigns, seomax, swiss) currently
 * have no exposed "is a job running right now?" signal in the client API
 * surface, so they remain `false` here. The shape is forward-compatible:
 * when a job-status endpoint lands for any of them, only this hook needs
 * to learn about it — the sidebar wiring already consumes the per-key flag.
 *
 * Polling cadence is tab-aware (`useVisibleInterval` pauses while hidden)
 * and adaptively faster while at least one service is active.
 */
export function useServiceActivity() {
  const { user } = useAuth();
  const { selectedProjectId } = useWorkspace();
  const [activity, setActivity] = useState({});

  const anyActive = useMemo(
    () => Object.values(activity).some(Boolean),
    [activity]
  );
  const pollMs = anyActive ? POLL_MS_ACTIVE : POLL_MS_QUIET;

  const probe = useCallback(async () => {
    if (!selectedProjectId || !user) {
      setActivity((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }

    const next = {};

    if (hasEntitlement(user, 'cwv')) {
      try {
        const { data } = await pulsescanAPI.listSessions(selectedProjectId, {
          status: 'queued,running',
          limit: 1,
        });
        const sessions = data?.data?.sessions || [];
        next.cwv = sessions.length > 0;
      } catch {
        next.cwv = false;
      }
    }

    if (hasEntitlement(user, 'ab_testing')) {
      try {
        const { data } = await experimentsAPI.getAll(selectedProjectId);
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.data?.experiments)
            ? data.data.experiments
            : [];
        next.ab_testing = list.some(
          (e) => String(e?.status || '').toLowerCase() === 'running'
        );
      } catch {
        next.ab_testing = false;
      }
    }

    setActivity((prev) => {
      const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
      let same = true;
      for (const k of keys) {
        if (!!prev[k] !== !!next[k]) {
          same = false;
          break;
        }
      }
      return same ? prev : next;
    });
  }, [selectedProjectId, user]);

  useVisibleInterval(probe, pollMs, {
    enabled: !!selectedProjectId && !!user,
    immediate: true,
  });

  return activity;
}

export default useServiceActivity;
